<template>
	<div id="visits-frequency">
		<!-- filters -->
		<v-row dense class="ma-0 px-4 pt-3">
			<v-col cols="12">
				<v-autocomplete
					v-model="entityType"
					:items="customersTypes"
					item-text="name"
					item-value="id"
					:return-object="false"
					:label="$t('inputs.customer-type')"
					dense
					hide-details
					append-icon
					outlined
					@change="filter(true, false)"
				></v-autocomplete>
			</v-col>
			<!-- entity name -->
			<v-col cols="6">
				<v-text-field
					v-model="entityName"
					:label="$T('inputs.customer-name')"
					dense
					hide-details
					outlined
					@keypress.down="filter(true, false)"
					@change="filter(true, false)"
				/>
			</v-col>

			<!-- region -->
			<v-col cols="6">
				<v-autocomplete
					v-model="regionId"
					:label="$T('inputs.region')"
					:items="regions"
					item-text="name"
					item-value="id"
					dense
					hide-details
					outlined
					@change="filter(true, false)"
				/>
			</v-col>
		</v-row>

		<!-- date range (from - to) -->
		<v-row dense class="ma-0 px-4">
			<!-- from date -->
			<v-col cols="6">
				<v-menu
					v-model="fromMenu"
					:close-on-content-click="false"
					transition="scale-transition"
					offset-y
					min-width="auto"
				>
					<template v-slot:activator="{ on, attrs }">
						<v-text-field
							v-model="fromDate"
							v-bind="attrs"
							:label="$T('inputs.from')"
							prepend-inner-icon="mdi-calendar"
							readonly
							dense
							hide-details
							outlined
							v-on="on"
						/>
					</template>
					<v-date-picker
						v-model="fromDate"
						:max="toDate"
						@change="fromMenu = false; filter(entities.length === 0, true)"
					/>
				</v-menu>
			</v-col>

			<!-- to date -->
			<v-col cols="6">
				<v-menu
					v-model="toMenu"
					:close-on-content-click="false"
					transition="scale-transition"
					offset-y
					min-width="auto"
				>
					<template v-slot:activator="{ on, attrs }">
						<v-text-field
							v-model="toDate"
							v-bind="attrs"
							:label="$T('inputs.to')"
							prepend-inner-icon="mdi-calendar"
							readonly
							dense
							hide-details
							outlined
							v-on="on"
						/>
					</template>
					<v-date-picker
						v-model="toDate"
						:min="fromDate"
						@change="toMenu = false; filter(entities.length === 0, true)"
					/>
				</v-menu>
			</v-col>
		</v-row>

		<!-- table -->
		<v-container>
			<v-data-table
				ref="table"
				:headers="headers"
				:items="loading ? [] : tableItems"
				:items-per-page="-1"
				:height="$vuetify.breakpoint.height - 285"
				:loading="loading"
				fixed-header
				hide-default-footer
				mobile-breakpoint="0"
				disable-sort
				dense
				class="elevation-3"
			>
				<!-- entity name cell -->
				<template v-slot:[`item.entityName`]="{ item }">
					<div class="d-flex align-center justify-center fixed-col--cell">
						<span class="text-truncate" style="max-width: 72px;">{{ item.entityName }}</span>
					</div>
				</template>

				<!-- total visits per row -->
				<template v-slot:[`item.totalVisits`]="{ item }">
					{{ $options.filters.formatNumber(item.totalVisits, 3) }}
				</template>

				<template
					v-for="col in dayColumns"
					v-slot:[`item.${col.value}`]="{ item, value, header }"
				>
					<div
						:key="col.value"
						class="state-cell"
						:class="[
							{
								'success': value === visitStates.done,
								'red': value === visitStates.missed,
								'orange': value === visitStates.outOfPlan,
							}
						]"
					>
						{{item.daysCount ? $options.filters.formatNumber(item.daysCount[header.value], 3) : null}}
					</div>
				</template>
			</v-data-table>
		</v-container>
	</div>
</template>

<script>
import moment from 'moment';
import { mapState } from 'vuex';
export default {
	name: 'VisitsFrequency',

	data() {
		return {
			loading: false,
			fromMenu: false,
			toMenu: false,

			// filter data
			entityName: null,
			regionId: null,
			entityType: null,
			fromDate: moment().startOf('month').format('YYYY-MM-DD'),
			toDate: moment().format('YYYY-MM-DD'),

			// data
			tableItems: [],
			tempVisits: [],
			visitStates: {
				done: 1,
				outOfPlan: 2,
				missed: 3,
			},
		}
	},

	computed: {
		...mapState({
			regions: state => state.regions.regions,
			entities: state => state.entities.entities,
			customersTypes: state => state.entities.customersTypes,
		}),

		// day columns for each day in the selected [from, to] range (can span multiple months)
		dayColumns() {
			const cols = [];
			if (!this.fromDate || !this.toDate) return cols;

			const start = moment(this.fromDate, 'YYYY-MM-DD');
			const end = moment(this.toDate, 'YYYY-MM-DD');
			if (end.isBefore(start, 'day')) return cols;

			for (let d = start.clone(); d.isSameOrBefore(end, 'day'); d.add(1, 'day')) {
				cols.push({
					value: 'd' + d.format('YYYYMMDD'),
					text: d.format('ddd DD').replace(/ /g, '\n'),
				})
			}
			return cols;
		},

		headers() {
			const days = this.dayColumns.map(col => ({
				text: col.text,
				name: col.value,
				value: col.value,
				class: 'px-0 text-pre-wrap table-head',
				cellClass: 'px-0',
				align: 'center',
				divider: true,
				width: 40
			}));
			const headers = [
				{
					text: this.$T('headers.customer'),
					value: 'entityName',
					class: 'px-1 fixed-col fixed-col--head',
					cellClass: 'px-1 grey--text text--darken-2 fixed-col font-weight-bold',
					align: 'center',
					divider: true,
					width: 120
				},
				{
					text: this.$T('headers.visits'),
					value: 'totalVisits',
					class: 'px-1 fixed-col fixed-col--visits fixed-col--head',
					cellClass: 'px-1 grey--text text--darken-2 fixed-col fixed-col--visits font-weight-bold',
					align: 'center',
					divider: true,
					width: 56
				},
				...days
			];
			return headers;
		},
	},

	methods: {
		filter(updateEntities = false, updateVisits = false, isFirstLoad = false) {
			this.loading = true;
			return Promise.all([
				updateEntities
					? this.$store.dispatch('entities/fetchAll', {
						name: this.entityName,
						regionId: this.regionId,
						entityType: this.entityType
					})
					: null,
				!this.tempVisits.length || updateVisits
					? this.$store.dispatch('visits/fetchAllDueDates', {
						fromDate: this.fromDate,
						toDate: this.toDate,
					})
					: null
			]).then((data) => {
				const items = [];
				let entities = [];

				entities = data[0] || this.entities;
				if (!this.tempVisits.length || updateVisits) this.tempVisits = data[1] || [];
				const visits = this.tempVisits;

				// building table items
				entities.forEach(entity => {
					const entityVisits = visits.filter(c => {
						if (c.entityId !== entity.id) return false;
						const visitDate = moment(c.createdAt).format('YYYY-MM-DD');
						return visitDate >= this.fromDate && visitDate <= this.toDate;
					});
					if (entityVisits.length) {
						const obj = {};
						const daysKeysObj = {};

						entityVisits.forEach(visit => {
							const day = 'd' + moment(visit.createdAt).format('YYYYMMDD');

							// get visits count for each day
							if (day in daysKeysObj) {
								if (!obj.daysCount) obj.daysCount = {};
								obj.daysCount[day] = (obj.daysCount[day] || 1) + 1;
							}

							obj.entityName = visit.entity.name;
							daysKeysObj[day] = this.visitStates.done;
						})

						// include entity id and total visits count
						obj.entityId = entity.id;
						obj.totalVisits = entityVisits.length;

						items.push({ ...obj, ...daysKeysObj });
					} else {
						items.push({ entityName: entity.name, entityId: entity.id, totalVisits: 0 })
					}
				});

				this.tableItems = items;
			}).finally(() => {
				if (!isFirstLoad) this.loading = false;
			})
		},
		moment
	},

	created() {
		this.loading = true;
		Promise.all([
			this.$store.dispatch('regions/fetchAll'),
			!this.customersTypes.length ? this.$store.dispatch('entities/fetchEntityType') : null,
		]).finally(() => {
			this.loading = false;
		})
	}
}
</script>

<style lang="scss">
#visits-frequency {
	.state-cell {
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		color: white;
	}
	.fixed-col {
		position: sticky;
		left: 0;
		background: white;
		font-size: 12px;

		&--visits {
			left: 120px;
		}

		&--head {
			font-size: inherit;
			z-index: 3;
		}

		&--cell {
			display: flex;
			align-items: center;
			justify-content: flex-start;
			gap: .5rem;
		}
	}
	.table-head {
		line-height: 14px;
	}
	.v-data-table tr:nth-of-type(2n) {
		&, & .fixed-col {
			background: #eee;
		}
	}
}
</style>
